import { SelectOption } from '@common/types/common';
import { LanguageInfo } from '@common/types/country';
import { PaginationProps, PaginationReturn } from '@common/types/service';
import { Roles, UserList } from '@common/types/user';
import { ROLE_HIERARCHY } from '@constants/user';
import {
    BadgesAndLevels,
    BlacklistEntry,
    Colleague,
    EmailType,
    EventAnalyticsPerformance,
    EventAnalyticsTraffic,
    EventLevelBadgeRequirement,
    EventLevelBadgeRewards,
    EventPrinter,
    EventProps,
    EventTypes,
    IAttendee,
    IBreakOutGroup,
    IEvent,
    IEventCreation,
    IEventPermissions,
    IEventStatistics,
    IEventUser,
    IGroup,
    INewSubscriber,
    ISubscriberActivity,
    ISubscriberFollowUp,
    ISubscriberFollowUpRating,
    ISubscribersStatus,
    LogicalOperator,
    ManagementAnyEvent,
    ManagementEventListItem,
    ManagementOnSiteEvent,
    ManagementWebinarEvent,
    PublicOnSiteEvent,
    PublicWebinarEvent,
    RegistrationEventListItem,
    RegistrationStatus,
    ResendEmailSubscriberRecepientStatus,
    WaitingListEntry,
    WaitingListProps,
    WaitingListResponse,
} from '@event/types';
import { EVENT_TYPE_ABBREVIATION, getEventTypeFromCreation } from '@event/utils/event';
import { api } from '@services/api';
import { capitalizeFirstLetter, hasKeys } from '@utils/common';
import {
    createSmartQueryHook,
    getFilterString,
    getPaginationString,
    handleQueryStarted,
    isPaginationReturn,
} from '@utils/service';

type PaginatedProps = EventProps &
    Required<Pick<PaginationProps, 'page_size' | 'page'>> &
    Omit<PaginationProps, 'page_size' | 'page'>;

const eventApi = api.injectEndpoints({
    endpoints: (builder) => ({
        allEvents: builder.query<
            PaginationReturn<ManagementEventListItem> | ManagementEventListItem[],
            PaginationProps
        >({
            query: ({ filters, ...pagination }) =>
                `management/events/?${getPaginationString(pagination)}${filters ? getFilterString(filters) : ''}`,
            providesTags: ['Events'],
            transformResponse: (
                response:
                    | (ManagementEventListItem & { possible_subscribers?: string })[]
                    | PaginationReturn<ManagementEventListItem & { possible_subscribers?: string }>
            ) =>
                isPaginationReturn(response)
                    ? {
                          ...response,
                          results: response?.results.map((event) => {
                              const transformedEvent = {
                                  ...event,
                                  type: event.type.replace('_', '-') as EventTypes,
                                  possible_subscribers: event.possible_subscribers
                                      ? (event.possible_subscribers.split(',') as Roles[])
                                      : [],
                              };

                              return transformedEvent;
                          }),
                      }
                    : response?.map((event) => {
                          const transformedEvent = {
                              ...event,
                              type: event.type.replace('_', '-') as EventTypes,
                              possible_subscribers: event.possible_subscribers
                                  ? (event.possible_subscribers.split(',') as Roles[])
                                  : [],
                          };
                          return transformedEvent;
                      }),
        }),
        allPublicEvents: builder.query<
            PaginationReturn<RegistrationEventListItem> | RegistrationEventListItem[],
            PaginationProps
        >({
            query: ({ filters, ...pagination }) =>
                `registration/events/?${getPaginationString(pagination)}${filters ? getFilterString(filters) : ''}`,
            transformResponse: (response?: PaginationReturn<RegistrationEventListItem> | RegistrationEventListItem[]) =>
                isPaginationReturn(response)
                    ? {
                          ...response,
                          results: response.results.map((event) => ({
                              ...event,
                              type: event.type.replace('_', '-') as EventTypes,
                          })),
                      }
                    : response?.map((event) => ({
                          ...event,
                          type: event.type.replace('_', '-') as EventTypes,
                      })) ?? [],
            providesTags: ['PublicEvents'],
        }),
        getEvent: builder.query<ManagementOnSiteEvent | ManagementWebinarEvent, EventProps>({
            query: ({ type, id }) => `management/events/${type}/${id}/`,
            providesTags: (_result, _err, { type, id }) => [{ type: 'Events', id: type + '-' + id }],
            transformResponse: (
                response: (ManagementOnSiteEvent | ManagementWebinarEvent) & { possible_subscribers?: string }
            ) => ({
                ...response,
                possible_subscribers: response.possible_subscribers
                    ? (response.possible_subscribers.split(',') as Roles[])
                    : [],
            }),
        }),
        getPublicEvent: builder.query<
            PublicOnSiteEvent | PublicWebinarEvent,
            EventProps & { colleagueHash?: string | null; guestAccessHash?: string | null }
        >({
            query: ({ type, id, colleagueHash, guestAccessHash }) =>
                `public/events/${type}/${id}/?${colleagueHash ? `colleague_hash=${colleagueHash}&` : ''}${
                    guestAccessHash ? `guest_access_hash=${guestAccessHash}` : ''
                }`,
            providesTags: (_result, _err, { type, id }) => [
                { type: 'PublicEventSubscriptionStatus', id: type + '-' + id },
            ],
            transformResponse: (
                response: (PublicOnSiteEvent | PublicWebinarEvent) & { possible_subscribers?: string }
            ) => ({
                ...response,
                possible_subscribers: response.possible_subscribers
                    ? (response.possible_subscribers.split(',') as Roles[])
                    : [],
            }),
        }),
        getInviteColleagueURL: builder.query<{ bring_a_colleague_url: string }, EventProps>({
            query: ({ type, id }) => `events/${type}/${id}/invite-colleague/`,
            providesTags: (_res, _err, { type, id }) => [{ type: 'InviteColleagueURL', id: type + '-' + id }],
        }),
        getEventAttendees: builder.query<
            { results: IAttendee[]; count: number; queryset_ids: IAttendee['id'][] },
            EventProps & PaginationProps & { key?: string }
        >({
            query: ({ type, id, filters, ...pagination }) =>
                `management/events/${type}/${id}/subscribers/?${getPaginationString(pagination)}${
                    filters ? getFilterString(filters) : ''
                }`,
            providesTags: (_result, _err, { type, id, key = 'default' }) => [
                { type: 'EventAttendees', id: type + '-' + id },
            ],
        }),
        getEventFollowUps: builder.query<
            PaginationReturn<ISubscriberFollowUp> | ISubscriberFollowUp[],
            EventProps & PaginationProps
        >({
            query: ({ type, id, ...pagination }) =>
                `management/events/${type}/${id}/subscribers/follow-ups/?${getPaginationString(pagination)}`,
            providesTags: (_result, _err, { type, id }) => [{ type: 'EventFollowUps', id: type + '-' + id }],
        }),
        getEventFollowUpsByRating: builder.query<
            PaginationReturn<ISubscriberFollowUpRating> | ISubscriberFollowUpRating[],
            (EventProps & PaginationProps) | EventProps
        >({
            query: ({ type, id, ...pagination }) =>
                `management/events/${type}/${id}/subscribers/follow-ups/by-rating/?${
                    pagination ? getPaginationString(pagination as PaginationProps) : ''
                }`,
            providesTags: (_result, _err, { type, id }) => [{ type: 'EventFollowUpsRating', id: type + '-' + id }],
        }),
        getEventSubscribersByStatus: builder.query<ISubscribersStatus[], EventProps>({
            query: ({ type, id }) => `management/events/${type}/${id}/subscribers/by-status/`,
            providesTags: (_result, _err, { type, id }) => [{ type: 'EventSubscribersByStatus', id: type + '-' + id }],
        }),
        getEventSubscribersActivity: builder.query<
            PaginationReturn<ISubscriberActivity> | ISubscriberActivity[],
            (EventProps & PaginationProps) | EventProps
        >({
            query: ({ type, id, ...pagination }) =>
                `management/events/${type}/${id}/subscribers/activity/?${
                    pagination ? getPaginationString(pagination as PaginationProps) : ''
                }`,
            providesTags: (_result, _err, { type, id }) => [{ type: 'EventSubscribersActivity', id: type + '-' + id }],
        }),
        registerUser: builder.mutation<
            void,
            EventProps & {
                attend_dinner?: boolean;
                colleagueHash?: string | null;
                guestAccessHash?: string | null;
            }
        >({
            query: ({ type, id, colleagueHash, guestAccessHash, ...body }) => ({
                url: `registration/${EVENT_TYPE_ABBREVIATION[type]}/register/${id}/?${
                    colleagueHash ? `colleague_hash=${colleagueHash}&` : ''
                }${guestAccessHash ? `guest_access_hash=${guestAccessHash}` : ''}`,
                method: 'POST',
                body,
            }),
            invalidatesTags: (_result, _error, { type, id }) => [
                'PublicEvents',
                { type: 'PublicEventSubscriptionStatus', id: type + '-' + id },
                { type: 'EventWaitingList', id: type + '-' + id },
            ],
        }),
        unregisterUser: builder.mutation<
            void,
            EventProps & { colleagueHash?: string | null; guestAccessHash?: string | null }
        >({
            query: ({ type, id, colleagueHash, guestAccessHash }) => ({
                url: `registration/${EVENT_TYPE_ABBREVIATION[type]}/unregister/${id}/?${
                    colleagueHash ? `colleague_hash=${colleagueHash}&` : ''
                }${guestAccessHash ? `guest_access_hash=${guestAccessHash}` : ''}`,
                method: 'POST',
            }),
            invalidatesTags: (_result, _error, { type, id }) => [
                'PublicEvents',
                { type: 'PublicEventSubscriptionStatus', id: type + '-' + id },
                { type: 'EventWaitingList', id: type + '-' + id },
            ],
        }),
        createEvent: builder.mutation<{ id: number }, IEventCreation>({
            query: (body) => ({
                url: `management/events/${getEventTypeFromCreation(body)}/`,
                method: 'POST',
                body,
            }),
            invalidatesTags: ['Events', 'PublicEvents', 'MyEvents', 'EventOrganizers'],
        }),
        updateEvent: builder.mutation<
            ManagementOnSiteEvent | ManagementWebinarEvent,
            Partial<
                Omit<ManagementAnyEvent, 'possible_subscribers'> & {
                    possible_subscribers?: string;
                    organizers_user_ids?: number[];
                }
            >
        >({
            query: ({ type, id, ...body }) => ({
                url: `management/events/${type}/${id}/`,
                method: 'PUT',
                body: { ...body },
            }),
            invalidatesTags: (_result, _error, { type, id }) => [
                { type: 'PublicEvents', id: type + '-' + id },
                { type: 'EventSubscribersByStatus', id: type + '-' + id },
                { type: 'EventNotOrganizers', id: type + '-' + id },
                { type: 'EventWaitingList', id: type + '-' + id },
                'EventOrganizers',
            ],
        }),
        patchEvent: builder.mutation<
            void,
            Partial<
                Omit<ManagementAnyEvent, 'possible_subscribers'> & {
                    possible_subscribers?: string;
                    organizers_user_ids?: number[];
                }
            >
        >({
            query: ({ type, id, ...body }) => ({
                url: `management/events/${type}/${id}/`,
                method: 'PATCH',
                body,
            }),
            invalidatesTags: (_result, _error, { type, id }) => [
                { type: 'EventNotOrganizers', id: type + '-' + id },
                { type: 'Events', id: type + '-' + id },
                { type: 'EventSubscribersByStatus', id: type + '-' + id },
                { type: 'PublicEvents', id: type + '-' + id },
                { type: 'EventWaitingList', id: type + '-' + id },
                'EventOrganizers',
            ],
        }),
        patchEventPublish: builder.mutation<void, EventProps & { published: boolean }>({
            query: ({ type, id, ...body }) => ({
                url: `management/events/${type}/${id}/publish/`,
                method: 'PATCH',
                body,
            }),
            invalidatesTags: (_result, _error, { type, id }) => [
                { type: 'Events', id: type + '-' + id },
                { type: 'PublicEvents', id: type + '-' + id },
                'EventOrganizers',
            ],
        }),
        deleteEvent: builder.mutation<void, EventProps>({
            query: ({ type, id }) => ({
                url: `management/events/${type}/${id}/`,
                method: 'DELETE',
            }),
            invalidatesTags: ['Events', 'PublicEvents', 'EventOrganizers', 'MyEvents', 'NewEventOwnerCandidates'],
        }),
        patchEventSubscriber: builder.mutation<
            IAttendee,
            EventProps & {
                subscriber_id: number;
                status?: IAttendee['status'];
                present?: IAttendee['present'];
                application_status?: IAttendee['application_status'];
            }
        >({
            query: ({ type, id, subscriber_id, ...body }) => ({
                url: `management/events/${type}/${id}/subscribers/${subscriber_id}/`,
                method: 'PATCH',
                body,
            }),
            invalidatesTags: (_res, _err, { type, id }) => [
                { type: 'EventAttendees', id: type + '-' + id },
                { type: 'EventSubscribersByStatus', id: type + '-' + id },
                { type: 'EventSubscribersActivity', id: type + '-' + id },
                { type: 'Events', id: type + '-' + id },
                { type: 'EventWaitingList', id: type + '-' + id },
            ],
        }),
        patchEventSubscribers: builder.mutation<
            IAttendee[],
            EventProps & {
                invalidateTags?: boolean;
                body: {
                    id: number;
                    status?: IAttendee['status'];
                    present?: IAttendee['present'];
                    group_id?: IBreakOutGroup['id'];
                    application_status?: IAttendee['application_status'];
                }[];
            }
        >({
            query: ({ type, id, body }) => ({
                url: `management/events/${type}/${id}/subscribers/`,
                method: 'PATCH',
                body,
            }),
            invalidatesTags: (_res, _err, { type, id, invalidateTags = true }) =>
                invalidateTags
                    ? [
                          { type: 'EventAttendees', id: type + '-' + id },
                          { type: 'EventStatistics', id: type + '-' + id },
                          { type: 'EventSubscribersByStatus', id: type + '-' + id },
                          { type: 'EventSubscribersActivity', id: type + '-' + id },
                          { type: 'Events', id: type + '-' + id },
                          { type: 'EventWaitingList', id: type + '-' + id },
                      ]
                    : [],
        }),
        sendEmail: builder.mutation<void, EventProps>({
            query: ({ id, type }) => ({
                url: `management/events/${type}/${id}/emails/invitation/send/`,
                method: 'POST',
            }),
            invalidatesTags: (_res, _err, { type, id }) => [
                { type: 'EventAttendees', id: type + '-' + id },
                { type: 'EventStatistics', id: type + '-' + id },
            ],
        }),
        resendEmail: builder.mutation<
            void,
            EventProps & { emailType: EmailType; subscriber_ids: number[]; invalidateTags?: boolean }
        >({
            query: ({ id, type, emailType, subscriber_ids }) => ({
                url: `management/events/${type}/${id}/emails/${emailType}/resend/`,
                method: 'POST',
                body: { subscriber_ids },
            }),
            invalidatesTags: (_res, _err, { type, id, invalidateTags = true }) =>
                invalidateTags
                    ? [
                          { type: 'EventAttendees', id: type + '-' + id },
                          { type: 'EventStatistics', id: type + '-' + id },
                      ]
                    : [],
        }),
        getEventStatistics: builder.query<IEventStatistics, EventProps>({
            query: ({ id, type }) => `management/events/${type}/${id}/statistics/`,
            providesTags: (_res, _err, { type, id }) => [{ type: 'EventStatistics', id: type + '-' + id }],
        }),
        getEventInvitationEmailTypes: builder.query<{ name: string; description: string }[], void>({
            query: () => 'management/events/invites/invitation-email-types',
            providesTags: () => ['EventInvitationEmailTypes'],
        }),
        getEventUsersNotSubscribed: builder.query<
            { count: number; results: IEventUser[]; queryset_ids: IEventUser['id'][] },
            EventProps & PaginationProps
        >({
            query: ({ id, type, filters, ...paginationProps }) =>
                `management/events/${type}/${id}/users-not-subscribed/?${getPaginationString(paginationProps)}${
                    filters ? getFilterString(filters) : ''
                }`,
            providesTags: (res, _err, { type, id }) =>
                res ? [{ type: 'EventUnsubscribers', id: type + '-' + id }] : [],
        }),
        getEventUsersNotOrganizers: builder.query<
            { count: number; results: IEventUser[]; queryset_ids: IEventUser['id'][] },
            EventProps & PaginationProps
        >({
            query: ({ id, type, filters, ...paginationProps }) =>
                `management/events/${type}/${id}/users-not-organizers/?${getPaginationString(paginationProps)}${
                    filters ? getFilterString(filters) : ''
                }`,
            providesTags: (res, _err, { type, id }) =>
                res ? [{ type: 'EventNotOrganizers', id: type + '-' + id }] : [],
        }),
        createEventSubscribers: builder.mutation<IAttendee[], EventProps & { newSubscribers: INewSubscriber[] }>({
            query: ({ type, id, newSubscribers }) => ({
                url: `management/events/${type}/${id}/subscribers/`,
                method: 'POST',
                body: newSubscribers,
            }),
            invalidatesTags: (_res, _err, { type, id }) => [
                { type: 'EventAttendees', id: type + '-' + id },
                { type: 'EventStatistics', id: type + '-' + id },
                { type: 'EventSubscribersByStatus', id: type + '-' + id },
                { type: 'EventSubscribersActivity', id: type + '-' + id },
                { type: 'Events', id: type + '-' + id },
                { type: 'EventWaitingList', id: type + '-' + id },
            ],
        }),
        addUsers: builder.mutation<
            IAttendee[],
            EventProps & {
                users: {
                    user?: INewSubscriber['user'];
                    group_id?: number;
                    user_id?: number;
                    status: RegistrationStatus;
                    present?: IAttendee['present'];
                }[];
            }
        >({
            query: ({ type, id, users }) => ({
                url: `management/events/${type}/${id}/subscribers/`,
                method: 'POST',
                body: users,
            }),
            invalidatesTags: (_res, _err, { type, id }) => [
                { type: 'EventAttendees', id: type + '-' + id },
                { type: 'EventStatistics', id: type + '-' + id },
                { type: 'EventSubscribersByStatus', id: type + '-' + id },
                { type: 'EventSubscribersActivity', id: type + '-' + id },
                { type: 'Events', id: type + '-' + id },
                { type: 'EventWaitingList', id: type + '-' + id },
                { type: 'EventUnsubscribers', id: type + '-' + id },
            ],
        }),
        deleteEventSubscribers: builder.mutation<void, EventProps & { subscriber_ids: number[] }>({
            query: ({ type, id, subscriber_ids }) => ({
                url: `management/events/${type}/${id}/subscribers/`,
                body: { subscriber_ids },
                method: 'DELETE',
            }),
            invalidatesTags: (_res, _err, { type, id }) => [
                { type: 'EventAttendees', id: type + '-' + id },
                { type: 'EventStatistics', id: type + '-' + id },
                { type: 'EventUnsubscribers', id: type + '-' + id },
                { type: 'EventSubscribersByStatus', id: type + '-' + id },
                { type: 'EventSubscribersActivity', id: type + '-' + id },
                { type: 'Events', id: type + '-' + id },
                { type: 'EventWaitingList', id: type + '-' + id },
            ],
        }),
        inviteUsers: builder.mutation<IAttendee | IAttendee[], EventProps & { users: { user_id: number }[] }>({
            query: ({ type, id, users }) => ({
                url: `management/events/${type}/${id}/subscribers/`,
                method: 'POST',
                body: users,
            }),
            invalidatesTags: (res, _err, { type, id }) =>
                res
                    ? [
                          { type: 'EventAttendees', id: type + '-' + id },
                          { type: 'EventUnsubscribers', id: type + '-' + id },
                          { type: 'EventStatistics', id: type + '-' + id },
                          { type: 'EventSubscribersByStatus', id: type + '-' + id },
                          { type: 'EventSubscribersActivity', id: type + '-' + id },
                          { type: 'Events', id: type + '-' + id },
                          { type: 'EventWaitingList', id: type + '-' + id },
                      ]
                    : [],
        }),
        replaceUser: builder.mutation<
            IAttendee,
            EventProps & {
                replace_subscriber_id: number;
                user_id?: number;
                user?: INewSubscriber['user'];
                status?: IAttendee['status'];
                group_id?: IBreakOutGroup['id'];
                present?: IAttendee['present'];
            }
        >({
            query: ({ type, id, ...body }) => ({
                url: `management/events/${type}/${id}/subscribers/`,
                method: 'POST',
                body,
            }),
            invalidatesTags: (res, _err, { type, id }) =>
                res
                    ? [
                          { type: 'EventAttendees', id: type + '-' + id },
                          { type: 'EventUnsubscribers', id: type + '-' + id },
                          { type: 'EventStatistics', id: type + '-' + id },
                          { type: 'EventSubscribersByStatus', id: type + '-' + id },
                          { type: 'EventSubscribersActivity', id: type + '-' + id },
                          { type: 'Events', id: type + '-' + id },
                          { type: 'EventWaitingList', id: type + '-' + id },
                          { type: 'EventUnsubscribers', id: type + '-' + id },
                      ]
                    : [],
        }),
        getEventGroupsAvailableToInvite: builder.query<IGroup[], EventProps>({
            query: ({ id, type }) => `management/events/${type}/${id}/subscribers/invite-group/available-to-invite/`,
            providesTags: (_res, _err, { type, id }) => [{ type: 'EventGroups', id: type + '-' + id }],
            onQueryStarted: (_arg, { queryFulfilled }) =>
                handleQueryStarted(queryFulfilled, 'Error fetching event groups'),
        }),
        createEventSubscribersFromGroup: builder.mutation<
            IGroup,
            EventProps & { group_id: IGroup['id']; group_type: IGroup['group_type'] }
        >({
            query: ({ type, id, group_id, ...body }) => ({
                url: `management/events/${type}/${id}/subscribers/invite-group/`,
                method: 'POST',
                body: {
                    id: group_id,
                    ...body,
                },
            }),
            invalidatesTags: (_res, _err, { type, id }) => [
                { type: 'EventAttendees', id: type + '-' + id },
                { type: 'EventGroups', id: type + '-' + id },
                { type: 'EventStatistics', id: type + '-' + id },
                { type: 'EventSubscribersByStatus', id: type + '-' + id },
                { type: 'EventSubscribersActivity', id: type + '-' + id },
                { type: 'Events', id: type + '-' + id },
                { type: 'EventWaitingList', id: type + '-' + id },
            ],
        }),
        addNonParticipant: builder.mutation<
            { guest_access_hash: string },
            EventProps & {
                email: string;
                first_name: string;
                last_name: string;
                company_name?: string;
                colleagueHash?: string | null;
                commit?: boolean;
                attend_dinner?: boolean;
            }
        >({
            query: ({ type, id, colleagueHash, commit, ...body }) => ({
                url: `public/events/${type}/${id}/subscribers/guest/${colleagueHash || commit === false ? '?' : ''}${
                    colleagueHash ? `colleague_hash=${colleagueHash}&` : ''
                }${commit === false ? 'commit=false' : ''}`,
                method: 'POST',
                body,
            }),
            invalidatesTags: (_res, _err, { type, id }) => [
                { type: 'PublicEvents', id: type + '-' + id },
                { type: 'PublicEventSubscriptionStatus', id: type + '-' + id },
                { type: 'EventWaitingList', id: type + '-' + id },
            ],
        }),
        patchUserPrimaryDetails: builder.mutation<
            Pick<IEventUser, 'id' | 'company_name' | 'country'> & { first_name: string; last_name: string },
            Pick<IEventUser, 'email' | 'company_name'> & { first_name: string; last_name: string } & EventProps & {
                    guestAccessHash?: string | null;
                }
        >({
            query: ({ id, type, guestAccessHash, ...body }) => ({
                url: `public/events/${type}/${id}/subscribers/user/${
                    guestAccessHash ? `?guest_access_hash=${guestAccessHash}` : ''
                }`,
                method: 'PATCH',
                body,
            }),
            invalidatesTags: (res, _err, { type, id }) =>
                res
                    ? [
                          { type: 'EventUnsubscribers', id: type + '-' + id },
                          { type: 'EventNotOrganizers', id: type + '-' + id },
                          'GuestUser',
                          'User',
                      ]
                    : [],
        }),
        addEventImage: builder.mutation<{ id: number; file: string; type: 'rich_text' }, EventProps & { file: Blob }>({
            query: ({ id, type, file }) => {
                const formData = new FormData();
                formData.append('type', 'rich_text');
                formData.append('file', file);

                return {
                    url: `management/events/${type}/${id}/images/`,
                    method: 'POST',
                    body: formData,
                };
            },
        }),
        getEventBreakOutGroups: builder.query<
            PaginationReturn<IBreakOutGroup> | IBreakOutGroup[],
            PaginationProps & EventProps
        >({
            query: ({ id, type, filters, ...pagination }) =>
                `management/events/${type}/${id}/groups/?${getPaginationString(pagination)}${
                    filters ? getFilterString(filters) : ''
                }`,
            providesTags: (res, _err, { id, type }) =>
                res ? [{ type: 'EventBreakOutGroups', id: type + '-' + id }] : [],
        }),
        createXEventBreakOutGroups: builder.mutation<void, EventProps & { num_groups: number }>({
            query: ({ id, type, ...body }) => ({
                url: `management/events/${type}/${id}/groups/default/`,
                method: 'POST',
                body,
            }),
            invalidatesTags: (res, _err, { id, type }) =>
                res ? [{ type: 'EventBreakOutGroups', id: type + '-' + id }] : [],
        }),
        createEventBreakOutGroup: builder.mutation<void, EventProps & { groups: Pick<IBreakOutGroup, 'name'>[] }>({
            query: ({ id, type, groups }) => ({
                url: `management/events/${type}/${id}/groups/`,
                method: 'POST',
                body: groups,
            }),
            invalidatesTags: (_res, err, { id, type }) =>
                !err
                    ? [
                          { type: 'EventBreakOutGroups', id: type + '-' + id },
                          { type: 'EventAttendees', id: type + '-' + id },
                      ]
                    : [],
        }),
        updateEventBreakOutGroup: builder.mutation<
            void,
            EventProps & { groups: Pick<IBreakOutGroup, 'id' | 'name'>[] }
        >({
            query: ({ id, type, groups }) => ({
                url: `management/events/${type}/${id}/groups/`,
                method: 'PATCH',
                body: groups,
            }),
            invalidatesTags: (_res, err, { id, type }) =>
                !err
                    ? [
                          { type: 'EventBreakOutGroups', id: type + '-' + id },
                          { type: 'EventAttendees', id: type + '-' + id },
                      ]
                    : [],
        }),
        deleteEventBreakOutGroup: builder.mutation<void, EventProps & { groups: number[] }>({
            query: ({ id, type, groups }) => ({
                url: `management/events/${type}/${id}/groups/?id=${groups.join('&id=')}`,
                method: 'DELETE',
            }),
            invalidatesTags: (_res, err, { id, type }) =>
                !err
                    ? [
                          { type: 'EventBreakOutGroups', id: type + '-' + id },
                          { type: 'EventAttendees', id: type + '-' + id },
                      ]
                    : [],
        }),
        copyEvent: builder.mutation<{ id: number; name: string }, EventProps>({
            query: ({ id, type }) => ({
                url: `management/events/${type}/${id}/copy/`,
                method: 'POST',
            }),
            invalidatesTags: ['Events', 'PublicEvents'],
        }),
        uploadEventBannerImage: builder.mutation<
            { banner_image: string },
            Partial<Pick<IEvent, 'id' | 'type'>> & { banner_image: File }
        >({
            query: ({ type, id, banner_image }) => {
                const formData = new FormData();
                formData.append('banner_image', banner_image);

                return {
                    url: `management/events/${type}/${id}/banner-images/`,
                    method: 'POST',
                    body: formData,
                };
            },
            invalidatesTags: (_result, _error, { type, id }) => [
                { type: 'Events', id: type + '-' + id },
                { type: 'PublicEvents', id: type + '-' + id },
            ],
        }),
        getEventCohostPermissions: builder.query<IEventPermissions[], EventProps>({
            query: ({ id, type }) => `management/events/${type}/${id}/permissions/`,
            providesTags: (_res, _err, { type, id }) => [{ type: 'EventPermissions', id: type + '-' + id }],
            transformResponse: (response: IEventPermissions[]) =>
                response.sort((a, b) => ROLE_HIERARCHY.indexOf(a.role) - ROLE_HIERARCHY.indexOf(b.role)),
        }),
        updateEventCohostPermission: builder.mutation<
            void,
            Partial<Pick<IEvent, 'id' | 'type'>> & { eventPermission: IEventPermissions }
        >({
            query: ({ type, id, eventPermission }) => ({
                url: `management/events/${type}/${id}/permissions/${eventPermission.id}/`,
                method: 'PATCH',
                body: eventPermission,
            }),
            invalidatesTags: (_result, _error, { type, id }) => [{ type: 'EventPermissions', id: type + '-' + id }],
        }),
        inviteColleague: builder.mutation<
            void,
            EventProps & { colleagues: Colleague[]; guestAccessHash?: string | null }
        >({
            query: ({ type, id, colleagues, guestAccessHash }) => ({
                url: `events/${type}/${id}/invite-colleague/${
                    guestAccessHash ? `?guest_access_hash=${guestAccessHash}` : ''
                }`,
                method: 'POST',
                body: [...colleagues],
            }),
        }),
        getLanguageSpokenOptions: builder.query<LanguageInfo[], void>({
            query: () => 'public/events/languages/',
            providesTags: (_res, _err) => [{ type: 'LanguageSpokenOptions' }],
        }),
        getBadgesAndLevelsOptions: builder.query<
            { badges: SelectOption<string>[]; levels: SelectOption<string>[] },
            void
        >({
            query: () => 'events/badges-and-levels/',
            providesTags: (_res, _err) => [{ type: 'BadgesAndLevelsOptions' }],
            transformResponse: (response) => {
                const { levels, badges } = response as BadgesAndLevels;
                const result = {
                    levels: levels.map((l) => ({ value: l.id.toString(), label: capitalizeFirstLetter(l.name) })),
                    badges: badges.map((b) => ({
                        value: b.id.toString(),
                        label: capitalizeFirstLetter(b.name),
                    })),
                };

                return result;
            },
        }),
        printBadges: builder.mutation<void, EventProps & { subscriber_ids: number[]; event_printer_id?: number }>({
            query: ({ type, id, subscriber_ids, event_printer_id }) => ({
                url: `management/events/${type}/${id}/subscribers/print-badge/${
                    event_printer_id ? `${event_printer_id}/` : ''
                }`,
                method: 'POST',
                body: { subscriber_ids },
            }),
        }),
        getCurrentEventRequiredEvents: builder.query<
            PaginationReturn<RegistrationEventListItem> | RegistrationEventListItem[],
            PaginationProps & EventProps
        >({
            query: ({ filters, type, id, ...pagination }) =>
                `events/${type}/${id}/level-badges/user-required-events/?${getPaginationString(pagination)}${
                    filters ? getFilterString(filters) : ''
                }`,
            transformResponse: (response?: PaginationReturn<RegistrationEventListItem> | RegistrationEventListItem[]) =>
                isPaginationReturn(response)
                    ? {
                          ...response,
                          results: response.results.map((event) => ({
                              ...event,
                              type: event.type.replace('_', '-') as EventTypes,
                          })),
                      }
                    : response?.map((event) => ({ ...event, type: event.type.replace('_', '-') as EventTypes })) ?? [],
            providesTags: ['UserRequiredEvents'],
        }),
        getUserRequiredSubscribedEvents: builder.query<
            PaginationReturn<RegistrationEventListItem> | RegistrationEventListItem[],
            PaginationProps & EventProps
        >({
            query: ({ filters, type, id, ...pagination }) =>
                `events/${type}/${id}/level-badges/user-required-subscribed-events/?${getPaginationString(pagination)}${
                    filters ? getFilterString(filters) : ''
                }`,
            transformResponse: (response?: PaginationReturn<RegistrationEventListItem> | RegistrationEventListItem[]) =>
                isPaginationReturn(response)
                    ? {
                          ...response,
                          results: response.results.map((event) => ({
                              ...event,
                              type: event.type.replace('_', '-') as EventTypes,
                          })),
                      }
                    : response?.map((event) => ({ ...event, type: event.type.replace('_', '-') as EventTypes })) ?? [],
            providesTags: ['UserRequiredSubscribedEvents'],
        }),
        getEventRewardedBadges: builder.query<EventLevelBadgeRewards[], EventProps>({
            query: ({ id, type }) => `management/events/${type}/${id}/level-badges/rewards/`,
            providesTags: (_res, _err, { type, id }) => [{ type: 'EventRewardedBadges', id: type + '-' + id }],
        }),
        createEventRewardedBadge: builder.mutation<
            void,
            EventProps & { badges: { level_id: number; badge_id: number }[] }
        >({
            query: ({ id, type, badges }) => ({
                url: `management/events/${type}/${id}/level-badges/rewards/`,
                method: 'POST',
                body: badges,
            }),
            invalidatesTags: (_res, _err, { type, id }) => [
                { type: 'EventRewardedBadges', id: type + '-' + id },
                { type: 'EventStatistics', id: type + '-' + id },
                'UserRequiredEvents',
            ],
        }),
        getEventRequiredBadges: builder.query<LogicalOperator | EventLevelBadgeRequirement | [], EventProps>({
            query: ({ id, type }) => `management/events/${type}/${id}/level-badges/requirements/`,
            providesTags: (_res, _err, { type, id }) => [{ type: 'EventRequiredBadges', id: type + '-' + id }],
        }),
        createEventRequiredBadge: builder.mutation<
            void,
            EventProps & { badges: { level_id: number; badge_id: number; timeframe: string }[] }
        >({
            query: ({ id, type, badges }) => ({
                url: `management/events/${type}/${id}/level-badges/requirements/`,
                method: 'POST',
                body: badges,
            }),
            invalidatesTags: (_res, _err, { type, id }) => [
                { type: 'EventRequiredBadges', id: type + '-' + id },
                { type: 'EventStatistics', id: type + '-' + id },
                'UserRequiredEvents',
            ],
        }),
        deleteLevelBadgeRequirement: builder.mutation<
            void,
            EventProps & { level_badge_requirement_id: EventLevelBadgeRequirement['id'] }
        >({
            query: ({ id, type, level_badge_requirement_id }) => ({
                url: `management/events/${type}/${id}/level-badges/requirements/${level_badge_requirement_id}/`,
                method: 'DELETE',
            }),
            invalidatesTags: (_res, _err, { type, id }) => [
                { type: 'EventRequiredBadges', id: type + '-' + id },
                { type: 'PublicEvents', id: type + '-' + id },
                { type: 'EventStatistics', id: type + '-' + id },
                'UserRequiredEvents',
            ],
        }),
        deleteLevelBadgeReward: builder.mutation<
            void,
            EventProps & { level_badge_reward_id: EventLevelBadgeRewards['id'] }
        >({
            query: ({ id, type, level_badge_reward_id }) => ({
                url: `management/events/${type}/${id}/level-badges/rewards/${level_badge_reward_id}/`,
                method: 'DELETE',
            }),
            invalidatesTags: (_res, _err, { type, id }) => [
                { type: 'EventRewardedBadges', id: type + '-' + id },
                { type: 'EventStatistics', id: type + '-' + id },
                'UserRequiredEvents',
            ],
        }),
        fetchSubscriberRecipients: builder.query<
            PaginationReturn<IAttendee>,
            EventProps &
                PaginationProps & {
                    emailType: EmailType;
                    subscriber_ids: number[];
                    status: ResendEmailSubscriberRecepientStatus;
                }
        >({
            query: ({ id, type, emailType, subscriber_ids, status, ...params }) => ({
                url: `management/events/${type}/${id}/emails/${emailType}/resend/check-candidates/${status}/`,
                params,
                method: 'POST',
                body: { subscriber_ids },
            }),
            providesTags: (_result, _err, { type, id }) => [
                { type: 'EventAttendeesResendRecipientStatus', id: type + '-' + id },
            ],
        }),
        joinWaitingList: builder.mutation<WaitingListResponse, WaitingListProps>({
            query: ({ type, id, colleagueHash, guestAccessHash, ...body }) => {
                return {
                    url: `events/${type}/${id}/waiting-list/entry/?${
                        colleagueHash ? `colleague_hash=${colleagueHash}&` : ''
                    }${guestAccessHash ? `guest_access_hash=${guestAccessHash}` : ''}`,
                    method: 'POST',
                    body,
                };
            },
            invalidatesTags: (_result, _error, { type, id }) => [
                'PublicEvents',
                { type: 'PublicEventSubscriptionStatus', id: type + '-' + id },
                { type: 'EventWaitingList', id: type + '-' + id },
            ],
        }),
        leaveWaitingList: builder.mutation<
            void,
            EventProps & {
                colleagueHash?: string | null;
                guestAccessHash?: string | null;
            }
        >({
            query: ({ type, id, colleagueHash, guestAccessHash, ...body }) => ({
                url: `events/${type}/${id}/waiting-list/entry/?${
                    colleagueHash ? `colleague_hash=${colleagueHash}&` : ''
                }${guestAccessHash ? `guest_access_hash=${guestAccessHash}` : ''}`,
                method: 'DELETE',
                body,
            }),
            invalidatesTags: (_result, _error, { type, id }) => [
                'PublicEvents',
                { type: 'PublicEventSubscriptionStatus', id: type + '-' + id },
                { type: 'EventWaitingList', id: type + '-' + id },
            ],
        }),
        getEventWaitingList: builder.query<
            PaginationReturn<WaitingListEntry> | WaitingListEntry[],
            EventProps & PaginationProps
        >({
            query: ({ filters, type, id, ...pagination }) =>
                `management/events/${type}/${id}/waiting-list/?${getPaginationString(pagination)}${
                    filters ? getFilterString(filters) : ''
                }`,
            providesTags: (_res, _err, { type, id }) => [{ type: 'EventWaitingList', id: type + '-' + id }],
        }),
        patchEventOwner: builder.mutation<void, EventProps & { owner_id: number }>({
            query: ({ id, type, owner_id }) => ({
                url: `events/${type}/${id}/owner/`,
                method: 'PATCH',
                body: { owner_id },
            }),
            invalidatesTags: (_res, _err, { type, id }) => [
                'Events',
                'PublicEvents',
                { type: 'EventNotOrganizers', id: type + '-' + id },
                { type: 'PublicEventSubscriptionStatus', id: type + '-' + id },
                'MyEvents',
                'NewEventOwnerCandidates',
                'EventOrganizers',
            ],
        }),
        getNewEventOwnerCandidates: builder.query<PaginationReturn<UserList>, EventProps & PaginationProps>({
            query: ({ id, type, filters, ...pagination }) =>
                `events/${type}/${id}/new-event-owner-candidates/?${getPaginationString(pagination)}${
                    filters ? getFilterString(filters) : ''
                }`,
            providesTags: ['NewEventOwnerCandidates'],
        }),
        getEventBlacklistEntries: builder.query<
            PaginationReturn<BlacklistEntry, BlacklistEntry['email']> | BlacklistEntry[],
            EventProps & PaginationProps
        >({
            query: ({ id, type, filters, ...pagination }) =>
                `events/blacklist/?${getPaginationString(pagination)}${filters ? getFilterString(filters) : ''}`,
            providesTags: (_res, _err, { type, id }) => [{ type: 'EventBlacklist', id: type + '-' + id }],
        }),
        removeEventBlacklistEntries: builder.mutation<void, EventProps & { emails: string[] }>({
            query: ({ id, type, emails }) => ({
                url: `events/${type}/${id}/blacklist/remove/`,
                method: 'DELETE',
                body: { emails },
            }),
            invalidatesTags: (_res, _err, { type, id }) => [
                { type: 'EventBlacklist', id: type + '-' + id },
                { type: 'EventUsersNotBlacklisted', id: type + '-' + id },
                'Events',
            ],
        }),
        createEventBlacklistEntries: builder.mutation<void, EventProps & { emails: { email: string }[] }>({
            query: ({ id, type, emails }) => ({
                url: `events/${type}/${id}/blacklist/`,
                method: 'POST',
                body: emails,
            }),
            invalidatesTags: (_res, _err, { type, id }) => [
                { type: 'EventBlacklist', id: type + '-' + id },
                { type: 'EventUsersNotBlacklisted', id: type + '-' + id },
                'Events',
            ],
        }),
        getEventUsersNotBlacklisted: builder.query<
            PaginationReturn<IEventUser, IEventUser['email']>,
            EventProps & PaginationProps
        >({
            query: ({ id, type, filters, ...paginationProps }) =>
                `events/${type}/${id}/users-not-blacklisted/?${getPaginationString(paginationProps)}${
                    filters ? getFilterString(filters) : ''
                }`,
            providesTags: (res, _err, { type, id }) =>
                res ? [{ type: 'EventUsersNotBlacklisted', id: type + '-' + id }] : [],
        }),
        getEventOrganizers: builder.query<UserList[], void>({
            query: () => 'events/organizers/',
            providesTags: (_res, _err) => ['EventOrganizers'],
        }),
        getEventPrinters: builder.query<PaginationReturn<EventPrinter>, EventProps & PaginationProps>({
            query: ({ filters, type, id, ...pagination }) =>
                `events/${type}/${id}/event-printers/?${getPaginationString(pagination)}${
                    filters ? getFilterString(filters) : ''
                }`,
            providesTags: (_res, _err, { type, id }) => [{ type: 'EventPrinters', id: type + '-' + id }],
        }),
        getEventAnalyticsTraffic: builder.query<EventAnalyticsTraffic, EventProps & PaginationProps>({
            query: ({ filters, type, id, ...pagination }) =>
                `management/events/${type}/${id}/analytics/traffic/?${getPaginationString(pagination)}${
                    filters ? getFilterString(filters) : ''
                }`,
            providesTags: (_res, _err, { type, id }) => [{ type: 'EventAnalyticsTraffic', id: type + '-' + id }],
        }),
        getEventAnalyticsPerformance: builder.query<
            EventAnalyticsPerformance,
            EventProps & { view_by?: 'week' | 'day' | 'month'; start_date?: string; end_date?: string }
        >({
            query: ({ id, type, ...params }) => ({
                url: `management/events/${type}/${id}/analytics/performance/`,
                params,
            }),
            providesTags: (_res, _err, { type, id }) => [{ type: 'EventAnalyticsPerformance', id: type + '-' + id }],
        }),
    }),
});

export const useSmartGetEventWaitingListQuery = createSmartQueryHook<
    EventProps,
    PaginatedProps,
    WaitingListEntry[],
    PaginationReturn<WaitingListEntry>,
    typeof eventApi.useGetEventWaitingListQuery
>(eventApi.useGetEventWaitingListQuery, hasKeys<EventProps & PaginatedProps>(['page_size', 'page']));

export const useSmartGetEventBlacklistEntriesQuery = createSmartQueryHook<
    EventProps,
    PaginatedProps,
    BlacklistEntry[],
    PaginationReturn<BlacklistEntry, BlacklistEntry['email']>,
    typeof eventApi.useGetEventBlacklistEntriesQuery
>(eventApi.useGetEventBlacklistEntriesQuery, hasKeys<EventProps & PaginatedProps>(['page_size', 'page']));

export const useSmartAllEventsQuery = createSmartQueryHook<
    PaginationProps,
    PaginationProps,
    ManagementEventListItem[],
    PaginationReturn<ManagementEventListItem, string>,
    typeof eventApi.useAllEventsQuery
>(eventApi.useAllEventsQuery, hasKeys<PaginationProps>(['page_size', 'page']));

export const useSmartGetEventBreakOutGroupsQuery = createSmartQueryHook<
    EventProps,
    PaginatedProps,
    IBreakOutGroup[],
    PaginationReturn<IBreakOutGroup>,
    typeof eventApi.useGetEventBreakOutGroupsQuery
>(eventApi.useGetEventBreakOutGroupsQuery, hasKeys<EventProps & PaginatedProps>(['page_size', 'page']));

export const {
    useGetInviteColleagueURLQuery,
    useAllEventsQuery,
    useAllPublicEventsQuery,
    useLazyAllPublicEventsQuery,
    useUpdateEventMutation,
    useDeleteEventMutation,
    useGetEventQuery,
    useCreateEventMutation,
    useGetEventAttendeesQuery,
    useRegisterUserMutation,
    useUnregisterUserMutation,
    useGetPublicEventQuery,
    useLazyGetPublicEventQuery,
    useLazyGetEventAttendeesQuery,
    usePatchEventSubscriberMutation,
    useResendEmailMutation,
    useGetEventStatisticsQuery,
    useGetEventInvitationEmailTypesQuery,
    useGetEventUsersNotSubscribedQuery,
    useCreateEventSubscribersMutation,
    useGetEventGroupsAvailableToInviteQuery,
    useCreateEventSubscribersFromGroupMutation,
    usePatchEventSubscribersMutation,
    useAddEventImageMutation,
    useInviteUsersMutation,
    useCreateEventBreakOutGroupMutation,
    useUpdateEventBreakOutGroupMutation,
    useDeleteEventBreakOutGroupMutation,
    useCreateXEventBreakOutGroupsMutation,
    useCopyEventMutation,
    useDeleteEventSubscribersMutation,
    useSendEmailMutation,
    useGetEventFollowUpsQuery,
    useUploadEventBannerImageMutation,
    useGetEventFollowUpsByRatingQuery,
    useGetEventCohostPermissionsQuery,
    useUpdateEventCohostPermissionMutation,
    useInviteColleagueMutation,
    useAddNonParticipantMutation,
    useGetEventSubscribersActivityQuery,
    useGetEventSubscribersByStatusQuery,
    useGetEventUsersNotOrganizersQuery,
    usePatchEventMutation,
    usePatchEventPublishMutation,
    useLazyGetEventQuery,
    usePatchUserPrimaryDetailsMutation,
    useGetLanguageSpokenOptionsQuery,
    usePrintBadgesMutation,
    useGetBadgesAndLevelsOptionsQuery,
    useGetEventRewardedBadgesQuery,
    useCreateEventRewardedBadgeMutation,
    useGetEventRequiredBadgesQuery,
    useCreateEventRequiredBadgeMutation,
    useGetCurrentEventRequiredEventsQuery,
    useDeleteLevelBadgeRequirementMutation,
    useDeleteLevelBadgeRewardMutation,
    usePatchEventOwnerMutation,
    useGetUserRequiredSubscribedEventsQuery,
    useJoinWaitingListMutation,
    useLeaveWaitingListMutation,
    useGetNewEventOwnerCandidatesQuery,
    useFetchSubscriberRecipientsQuery,
    useLazyFetchSubscriberRecipientsQuery,
    useGetEventOrganizersQuery,
    useGetEventPrintersQuery,
    usePrefetch,
    useRemoveEventBlacklistEntriesMutation,
    useCreateEventBlacklistEntriesMutation,
    useGetEventUsersNotBlacklistedQuery,
    useGetEventAnalyticsTrafficQuery,
    useGetEventAnalyticsPerformanceQuery,
    useReplaceUserMutation,
    useAddUsersMutation,
    useGetEventBreakOutGroupsQuery,
} = eventApi;
export default eventApi;
